import { blobticService } from '../../Commun/services/blobticService';
import { MaterialType } from './windowsApi';

export interface ProfileDto {
    PRF_IDF: number;
    PRF_LIB: string;
    PRF_CODE?: string;
    PRF_TYPE?: number; // Should map to MaterialType? Need validation
    // Based on Maker_Profile_DM.cs: PRF_TYPE is int.
    // Legacy profilesApi.ts interface: materialType: MaterialType
    PRF_DESCRIPTION?: string;
    PRF_WIDTH: number;
    PRF_HEIGHT: number;
    PRF_DEPTH: number;
    PRF_PRICE: number;
    PRF_STOCK: number;
    PRF_PHOTO?: string;
    GAM_IDF?: number;
    LAST_UPDT?: string;
    // isActive missing in DM? Assuming active if exists or need new field.
}

export interface Profile {
    id: number;
    name: string;
    code?: string;
    materialType: MaterialType;
    description?: string;
    width: number;
    height: number;
    depth: number;
    unitPrice: number;
    stockQuantity: number;
    photo?: string;
    gammeId?: number;
    isActive: boolean;
    createdDate: string;
    modifiedDate?: string;
}

const mapDtoToModel = (dto: ProfileDto): Profile => ({
    id: dto.PRF_IDF,
    name: dto.PRF_LIB,
    code: dto.PRF_CODE,
    materialType: dto.PRF_TYPE as MaterialType, // Assuming type matches
    description: dto.PRF_DESCRIPTION,
    width: dto.PRF_WIDTH,
    height: dto.PRF_HEIGHT,
    depth: dto.PRF_DEPTH,
    unitPrice: dto.PRF_PRICE,
    stockQuantity: dto.PRF_STOCK,
    photo: dto.PRF_PHOTO,
    gammeId: dto.GAM_IDF,
    isActive: true, // Defaulting to true as no status field in current DM
    createdDate: dto.LAST_UPDT || new Date().toISOString(),
    modifiedDate: dto.LAST_UPDT
});

const mapModelToDto = (model: Partial<Profile>): Partial<ProfileDto> => ({
    PRF_IDF: model.id,
    PRF_LIB: model.name,
    PRF_CODE: model.code,
    PRF_TYPE: model.materialType,
    PRF_DESCRIPTION: model.description,
    PRF_WIDTH: model.width,
    PRF_HEIGHT: model.height,
    PRF_DEPTH: model.depth,
    PRF_PRICE: model.unitPrice,
    PRF_STOCK: model.stockQuantity,
    PRF_PHOTO: model.photo,
    GAM_IDF: model.gammeId,
});

export const profilesApi = {
    getAll: async () => {
        const response = await blobticService.executeZoomRequest<ProfileDto[]>({
            ZoomKey: 'Blobtic_Maker',
            Action: { OKey: 'Blobtic_Maker,maker_profile,Select' }
        });
        return { data: (response.data || []).map(mapDtoToModel) };
    },

    getById: async (id: number) => {
        const response = await blobticService.executeZoomRequest<ProfileDto[]>({
            ZoomKey: 'Blobtic_Maker',
            Action: {
                OKey: 'Blobtic_Maker,maker_profile,Select',
                PRF_IDF: id
            }
        });
        const dto = response.data?.[0];
        return { data: dto ? mapDtoToModel(dto) : null };
    },

    create: async (profile: Partial<Profile>) => {
        const dto = mapModelToDto(profile);
        const response = await blobticService.executeZoomRequest<ProfileDto[]>({
            ZoomKey: 'Blobtic_Maker',
            Action: {
                OKey: 'Blobtic_Maker,maker_profile,Insert',
                ...dto
            }
        });
        return {
            data: response.data?.[0] ? mapDtoToModel(response.data[0]) : null,
            message: response.message
        };
    },

    update: async (id: number, profile: Partial<Profile>) => {
        const dto = mapModelToDto(profile);
        const response = await blobticService.executeZoomRequest<ProfileDto[]>({
            ZoomKey: 'Blobtic_Maker',
            Action: {
                OKey: 'Blobtic_Maker,maker_profile,Update',
                PRF_IDF: id,
                ...dto
            }
        });
        return {
            data: response.data?.[0] ? mapDtoToModel(response.data[0]) : null,
            message: response.message
        };
    },

    delete: async (id: number) => {
        const response = await blobticService.executeZoomRequest({
            ZoomKey: 'Blobtic_Maker',
            Action: {
                OKey: 'Blobtic_Maker,maker_profile,Delete',
                PRF_IDF: id
            }
        });
        return {
            success: true,
            message: response.message
        };
    }
};
